"use client";

import type { KeyboardEvent } from "react";
import { ArrowUp, FileText, Loader2, Mic, Paperclip, Sparkles, Square, X } from "lucide-react";
import { toast } from "sonner";
import {
    Conversation,
    ConversationContent,
    ConversationScrollButton,
} from "@/components/ui/shadcn-io/ai/conversation";
import { AssistantMessageContent } from "@/components/aios-command/chat/message-renderer";
import { ACCEPT_FILES } from "@/hooks/use-file-attachment-queue";
import { cn } from "@/lib/utils";
import { CommandIcon } from "./command-icon";
import { useCommandPanel } from "./command-panel-provider";

function MessagesErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
            <p className="text-sm text-muted-foreground">Couldn&apos;t load this conversation.</p>
            <button type="button" onClick={onRetry} className="text-sm font-medium text-primary hover:underline">
                Try again
            </button>
        </div>
    );
}

function EmptyThreadState({ agentName }: { agentName: string }) {
    return (
        <div className="mb-auto pt-10 text-center">
            <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-primary/10">
                <CommandIcon className="size-5 text-primary" />
            </span>
            <p className="mt-3 text-sm font-semibold">Talk to {agentName}</p>
            <p className="mx-auto mt-1 max-w-[260px] text-sm text-muted-foreground">
                Ask anything about your workspace, {agentName} will help you get it done.
            </p>
        </div>
    );
}

export function CommandPanelThread() {
    const { thread, activeAgent } = useCommandPanel();
    const {
        messages,
        inputValue,
        setInputValue,
        isTyping,
        streamingMessageId,
        handleSubmit,
        isLoadingMessages,
        isMessagesError,
        retryMessages,
        conversationRef,
        inputRef,
        stickyContextRef,
        loadMoreRef,
        hasNextPage,
        attachedFiles,
        isDraggingOver,
        fileInputRef,
        handleFileSelect,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
        removeAttachedFile,
    } = thread;

    const isStreaming = isTyping || Boolean(streamingMessageId);
    const hasUploadedFile = attachedFiles.some((a) => a.status === "done");

    const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="flex h-full flex-col" ref={conversationRef}>
            {isMessagesError ? (
                <MessagesErrorState onRetry={retryMessages} />
            ) : messages.length === 0 && !isLoadingMessages ? (
                <EmptyThreadState agentName={activeAgent?.name ?? "Command"} />
            ) : (
                <Conversation className="flex-1" contextRef={stickyContextRef}>
                    <ConversationContent className="space-y-4">
                        {hasNextPage ? <div ref={loadMoreRef} /> : null}
                        {messages.map((message) => {
                            if (message.role === "user") {
                                return message.content?.trim() ? (
                                    <div
                                        key={message.id}
                                        className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-muted px-4 py-2.5 text-sm leading-relaxed"
                                    >
                                        {message.content}
                                    </div>
                                ) : null;
                            }

                            const isWaitingForFirstChunk =
                                message.isStreaming &&
                                !message.content?.trim() &&
                                (message.contentSegments?.length ?? 0) === 0;

                            return (
                                <div key={message.id} className="mr-auto flex w-full max-w-[92%] items-start gap-2.5">
                                    <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                        <Sparkles className="size-3" />
                                    </span>
                                    {isWaitingForFirstChunk ? (
                                        <span className="flex items-center gap-1 py-1.5">
                                            {[0, 150, 300].map((delay) => (
                                                <span
                                                    key={delay}
                                                    className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60"
                                                    style={{ animationDelay: `${delay}ms` }}
                                                />
                                            ))}
                                        </span>
                                    ) : (
                                        <div className="min-w-0 flex-1 text-sm leading-relaxed">
                                            <AssistantMessageContent
                                                content={message.content}
                                                contentSegments={message.contentSegments}
                                                tools={message.tools}
                                                isStreaming={message.isStreaming}
                                                messageId={message.id}
                                                status={message.status}
                                                streamError={message.streamError}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </ConversationContent>
                    <ConversationScrollButton />
                </Conversation>
            )}

            <div className="shrink-0 border-t px-3 py-2.5">
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                        "command-spin-outline rounded-3xl border bg-card px-4 py-3 shadow-sm",
                        isDraggingOver && "border-primary/50 bg-primary/5"
                    )}
                >
                    {attachedFiles.length > 0 ? (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                            {attachedFiles.map((attachment, index) => (
                                <span
                                    key={attachment.localId}
                                    className="inline-flex items-center gap-1.5 rounded-lg border bg-muted/50 px-2 py-1 text-xs"
                                >
                                    {attachment.status === "uploading" ? (
                                        <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                                    ) : (
                                        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                                    )}
                                    <span className="max-w-[160px] truncate">{attachment.file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachedFile(index)}
                                        className="text-muted-foreground transition-colors hover:text-foreground"
                                        aria-label={`Remove ${attachment.file.name}`}
                                    >
                                        <X className="size-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : null}
                    <textarea
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={onKeyDown}
                        onPaste={handlePaste}
                        rows={2}
                        placeholder="Ask Command anything…"
                        className="w-full resize-none bg-transparent text-base leading-relaxed outline-none placeholder:text-muted-foreground lg:text-sm"
                    />
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label="Attach files"
                            title="Attach a file"
                        >
                            <Paperclip className="size-4" />
                        </button>
                        {isStreaming || inputValue.trim() || hasUploadedFile ? (
                            <button
                                type="button"
                                onClick={() => handleSubmit()}
                                aria-label={isStreaming ? "Stop generating" : "Send"}
                                className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
                            >
                                {isStreaming ? (
                                    <Square className="size-3.5 fill-current" />
                                ) : (
                                    <ArrowUp className="size-4" />
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() =>
                                    toast.info("Voice input isn't available yet", {
                                        description: "Type your message instead.",
                                    })
                                }
                                className="flex size-9 cursor-not-allowed items-center justify-center rounded-full bg-muted text-muted-foreground opacity-60"
                                aria-label="Voice input (not available yet)"
                                title="Voice input isn't available yet"
                            >
                                <Mic className="size-4" />
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPT_FILES}
                        className="hidden"
                        onChange={handleFileSelect}
                    />
                </div>
                <p className="pb-1 pt-2 text-center text-xs text-muted-foreground">
                    Command can make mistakes. Double-check replies.
                </p>
            </div>
        </div>
    );
}
